/* eslint-disable @typescript-eslint/no-unused-vars */
/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useEffect, useState } from "react";
import { Loader } from "./components/Loader";
import { useAuth } from "./contexts/AuthContext";
import { QRCodeSVG } from 'qrcode.react';
import jsPDF from 'jspdf';
import { ENDPOINTS } from "./endpoints";
import { temPermissao } from "./utils/permissoes";

// Tipo para departamento
interface Departamento {
  id: number;
  descricao: string;
}

interface ListaItem {
  departamento: string;
  local: string;
  url: string;
}

export default function QRCodeGenerator() {
  const { loginUnidade, permissoes } = useAuth();
  const [departamentos, setDepartamentos] = useState<Departamento[]>([]);
  const [modoFixo, setModoFixo] = useState(true);
  const [departamento, setDepartamento] = useState("");
  const [local, setLocal] = useState("");
  const [carregando, setCarregando] = useState(false);
  const [lista, setLista] = useState<ListaItem[]>([]);
  const [selecionado, setSelecionado] = useState<number | null>(null);
  const [processando, setProcessando] = useState(false);
  const [confirmarLimpeza, setConfirmarLimpeza] = useState(false);
  const [erros, setErros] = useState<{departamento?: boolean, local?: boolean}>({});
  const [qrCodeUrl, setQrCodeUrl] = useState("");

  const podeAcessar = temPermissao(permissoes, "qrcode", "acessar_modulo");
  if (!podeAcessar) {
    return <div className="p-8 text-center text-red-600 font-bold text-xl">Você não tem permissão para acessar este módulo.</div>;
  }

  useEffect(() => {
    async function fetchDepartamentos() {
      if (!loginUnidade) return;
      setCarregando(true);
      try {
        const response = await fetch(ENDPOINTS.DEPARTAMENTO_LIST, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ unidade: loginUnidade.toLowerCase() })
        });
        const data = await response.json();
        setDepartamentos(Array.isArray(data) ? data : []);
      } catch (err) {
        setDepartamentos([]);
      }
      setCarregando(false);
    }
    fetchDepartamentos();
  }, [loginUnidade]);

  function gerarUrl(dep: string, loc: string) {
    const base = `https://bot.advserv.app.br/${(loginUnidade || '').toLowerCase()}-os`;
    const params = [];
    if (dep) params.push(`departamento=${encodeURIComponent(dep)}`);
    if (loc) params.push(`local=${encodeURIComponent(loc)}`);
    return `${base}?${params.join("&")}`;
  }

  function handleAdicionar() {
    if (modoFixo) {
      const novoErros: {departamento?: boolean, local?: boolean} = {};
      if (!departamento) novoErros.departamento = true;
      if (!local) novoErros.local = true;
      setErros(novoErros);
      if (!departamento || !local) return;
    }
    setProcessando(true);
    setTimeout(() => {
      if (modoFixo) {
        const url = gerarUrl(departamento, local);
        setLista(prev => [...prev, { departamento, local, url }]);
        setLocal("");
        setSelecionado(null);
        setProcessando(false);
      } else {
        // SEM departamento/local fixo: só gera a base da URL
        const url = `https://bot.advserv.app.br/${(loginUnidade || '').toLowerCase()}-os`;
        setLista(prev => [...prev, { departamento: '', local, url }]);
        setLocal("");
        setSelecionado(null);
        setProcessando(false);
      }
    }, 400);
  }

  function handleApagar(idx: number) {
    setProcessando(true);
    setTimeout(() => {
      setLista(lista.filter((_, i) => i !== idx));
      setSelecionado(sel => sel === idx ? null : sel);
      setProcessando(false);
    }, 400);
  }

  function handleLimpar() {
    setConfirmarLimpeza(true);
  }

  function confirmarLimparLista() {
    setProcessando(true);
    setTimeout(() => {
      setLista([]);
      setSelecionado(null);
      setProcessando(false);
      setConfirmarLimpeza(false);
    }, 400);
  }

  // Função para gerar o PDF
  async function handleGerarPDF() {
    if (lista.length === 0) return;
    setProcessando(true);
    setTimeout(async () => {
      try {
        const doc = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });
        const marginX = 18;
        const marginY = 18;
        const qrSize = 60;
        const cellWidth = 85;
        const cellHeight = 90;
        const cols = 2;
        let x = marginX;
        let y = marginY;
        for (let i = 0; i < lista.length; i++) {
          const item = lista[i];
          const qrUrl = `https://api.qrserver.com/v1/create-qr-code/?size=${qrSize}x${qrSize}&data=${encodeURIComponent(item.url)}`;
          doc.addImage(qrUrl, 'PNG', x + (cellWidth - qrSize) / 2, y, qrSize, qrSize);
          doc.setFontSize(13);
          doc.setFont('helvetica', 'bold');
          doc.text(item.departamento, x + cellWidth / 2, y + qrSize + 10, { align: 'center', maxWidth: cellWidth - 4 });
          doc.setFontSize(12);
          doc.setFont('helvetica', 'normal');
          doc.text(item.local, x + cellWidth / 2, y + qrSize + 20, { align: 'center', maxWidth: cellWidth - 4 });
          x += cellWidth;
          if ((i + 1) % cols === 0) {
            x = marginX;
            y += cellHeight;
            if ((i + 1) % 6 === 0 && i !== lista.length - 1) {
              doc.addPage();
              y = marginY;
            }
          }
        }
        doc.save('qrcodes.pdf');
      } finally {
        setProcessando(false);
      }
    }, 50);
  }

  async function handleGerarQRCode() {
    if (!loginUnidade) return;
    setProcessando(true);
    try {
      const response = await fetch(ENDPOINTS.QRCODE_GENERATE, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ unidade: loginUnidade.toLowerCase() })
      });
      const data = await response.json();
      if (data.url) {
        console.log("QR Code gerado:", data.url);
      }
    } catch (error) {
      console.error("Erro ao gerar QR Code:", error);
    } finally {
      setProcessando(false);
    }
  }

  return (
    <div className="w-full h-full bg-[#f5f6f8] p-0">
      {processando && <Loader />}
      <div className="w-full h-full bg-white rounded-none shadow-none p-8 overflow-auto">
        <h2 className="text-2xl font-bold mb-6 text-black text-left">GERAR QRCODE (BOT AUTO ATENDIMENTO)</h2>
        <div className="flex flex-col md:flex-row gap-12">
          <div className="flex-1 flex flex-col gap-6 justify-start">
            <div className="flex items-center justify-between mb-2">
              <div className="flex gap-8 items-center">
                <label className="flex items-center gap-2 text-black">
                  <input type="radio" checked={modoFixo} onChange={() => setModoFixo(true)} />
                  <span className="text-black">COM departamento e local fixo</span>
                </label>
                <label className="flex items-center gap-2 text-black">
                  <input type="radio" checked={!modoFixo} onChange={() => setModoFixo(false)} />
                  <span className="text-black">SEM departamento e local fixo, usuário vai informar</span>
                </label>
              </div>
              {/* Botões de ação sempre visíveis no canto superior direito */}
              <div className="flex gap-3">
                <button
                  className="bg-green-600 hover:bg-green-700 text-white rounded-full w-10 h-10 flex items-center justify-center shadow transition-colors cursor-pointer"
                  onClick={handleAdicionar}
                  title="Adicionar à Lista"
                  disabled={processando}
                >
                  <i className="fa fa-plus text-lg"></i>
                </button>
                <button
                  className="bg-gray-400 hover:bg-gray-500 text-white rounded-full w-10 h-10 flex items-center justify-center shadow transition-colors cursor-pointer"
                  onClick={handleLimpar}
                  title="Limpar Lista"
                  disabled={lista.length === 0 || processando}
                >
                  <i className="fa fa-broom text-lg"></i>
                </button>
                <button
                  className="bg-blue-600 hover:bg-blue-700 text-white rounded-full w-10 h-10 flex items-center justify-center shadow transition-colors cursor-pointer"
                  title="Gerar Impressão"
                  onClick={handleGerarPDF}
                  disabled={lista.length === 0 || processando}
                >
                  <i className="fa fa-print text-lg"></i>
                </button>
              </div>
            </div>
            {modoFixo && (
              <>
                <div>
                  <label className="block text-sm font-semibold mb-1 text-black">
                    Departamento <span style={{color: 'red'}}>*</span>
                  </label>
                  <select
                    className={`w-full rounded px-2 py-2 border text-gray-900 ${erros.departamento ? 'border-red-500' : 'border-gray-300'}`}
                    value={departamento}
                    onChange={e => { setDepartamento(e.target.value); setErros(erros => ({...erros, departamento: false})); }}
                    disabled={carregando}
                  >
                    {carregando ? (
                      <option value="">Carregando...</option>
                    ) : (
                      <option value="">Selecione...</option>
                    )}
                    {departamentos
                      .slice()
                      .sort((a, b) => a.descricao.localeCompare(b.descricao))
                      .map(dep => (
                        <option key={dep.id} value={dep.descricao}>{dep.descricao}</option>
                      ))}
                  </select>
                  {erros.departamento && (
                    <span className="text-red-500 text-xs mt-1">Campo obrigatório</span>
                  )}
                </div>
                <div className="flex items-end gap-2 mt-2">
                  <div className="flex-1">
                    <label className="block text-sm font-semibold mb-1 text-black">
                      Local <span style={{color: 'red'}}>*</span>
                    </label>
                    <input
                      className={`w-full rounded px-2 py-2 border text-gray-900 ${erros.local ? 'border-red-500' : 'border-gray-300'}`}
                      value={local}
                      onChange={e => { setLocal(e.target.value); setErros(erros => ({...erros, local: false})); }}
                      placeholder="Digite o local"
                    />
                    {erros.local && (
                      <span className="text-red-500 text-xs mt-1">Campo obrigatório</span>
                    )}
                  </div>
                </div>
              </>
            )}
            {/* Área da lista virtual */}
            <div className="mt-8 min-h-[60px] bg-gray-50 rounded p-2">
              {lista.length === 0 ? (
                <div className="text-center text-black/60">Lista virtual aparecerá aqui...</div>
              ) : (
                <table className="w-full text-sm">
                  <thead>
                    <tr className="bg-gray-200">
                      <th className="p-2 text-left text-black">Departamento</th>
                      <th className="p-2 text-left text-black">Local</th>
                      <th className="p-2 text-left text-black">URL do QRCode</th>
                      <th className="p-2 text-center text-black">Ações</th>
                    </tr>
                  </thead>
                  <tbody>
                    {lista.map((item, idx) => (
                      <tr
                        key={idx}
                        className={`cursor-pointer ${selecionado === idx ? 'bg-teal-200' : idx % 2 === 0 ? 'bg-white' : 'bg-gray-100'} hover:bg-teal-200 text-black`}
                        onClick={() => setSelecionado(idx)}
                      >
                        <td className="p-2 font-semibold">{item.departamento}</td>
                        <td className="p-2">{item.local}</td>
                        <td className="p-2 text-xs break-all">{item.url}</td>
                        <td className="p-2 text-center">
                          <button
                            className="bg-red-600 hover:bg-red-700 text-white rounded-full w-8 h-8 flex items-center justify-center shadow transition-colors cursor-pointer"
                            onClick={e => { e.stopPropagation(); handleApagar(idx); }}
                            title="Apagar da Lista"
                          >
                            <i className="fa fa-trash"></i>
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </div>
          {/* QRCode visualização */}
          <div className="md:w-96 flex flex-col items-center justify-center mt-8 md:mt-0">
            <div className="w-full bg-gray-100 rounded-lg shadow p-6 flex flex-col items-center min-h-[320px] min-w-[260px]">
              {selecionado !== null && lista[selecionado] ? (
                <>
                  <QRCodeSVG value={lista[selecionado].url} size={220} />
                  <div className="mt-6 text-xs break-all text-center text-black max-w-xs">
                    {lista[selecionado].url}
                  </div>
                </>
              ) : (
                <div className="flex flex-col items-center justify-center h-full text-black/60">
                  <i className="fa fa-qrcode text-6xl mb-4"></i>
                  <span>Selecione um item para visualizar o QRCode</span>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
      {/* Modal de confirmação de limpeza da lista virtual */}
      {confirmarLimpeza && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
          <style>{`
            .modal-anim {
              animation: zoomInFade 0.35s cubic-bezier(0.4, 0, 0.2, 1);
            }
            @keyframes zoomInFade {
              0% {
                opacity: 0;
                transform: scale(0.7);
              }
              100% {
                opacity: 1;
                transform: scale(1);
              }
            }
          `}</style>
          <div className="modal-anim bg-white rounded-lg shadow-2xl p-8 max-w-[95vw] w-full max-w-[400px] flex flex-col items-center">
            <svg width="80" height="80" viewBox="0 0 80 80" fill="none" className="mb-4">
              <circle cx="40" cy="40" r="38" stroke="#f87171" strokeWidth="4" fill="#fff" />
              <path d="M28 28L52 52" stroke="#f87171" strokeWidth="4" strokeLinecap="round" />
              <path d="M52 28L28 52" stroke="#f87171" strokeWidth="4" strokeLinecap="round" />
            </svg>
            <h3 className="text-2xl font-bold mb-2 text-black">Confirma limpeza?</h3>
            <p className="text-black mb-6 text-center">Deseja realmente excluir <b>TODOS</b> os itens da lista virtual?</p>
            <div className="flex gap-4 mt-2 w-full justify-center">
              <button
                onClick={confirmarLimparLista}
                className="bg-red-600 text-white px-6 py-2 rounded font-semibold hover:bg-red-700 transition-colors w-full sm:w-auto shadow"
              >
                Excluir
              </button>
              <button
                onClick={() => setConfirmarLimpeza(false)}
                className="bg-gray-500 text-white px-6 py-2 rounded font-semibold hover:bg-gray-600 transition-colors w-full sm:w-auto shadow"
              >
                Cancelar
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
} 